import { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash-es';
import {
  getGames,
  IS_USE_PLEX_API,
  PLEX_LIMIT,
  useCategories,
  useGamesData,
  useGetFeedCategoryGames,
  useGetLoadFeedCategoryCallback,
  useLoadCasinoFeed,
  useLoadCategory
} from '@namespace/games';

const usePromotionGamesNonPlex = ({ feedNames, feedCategory }) => {
  const { isLoaded } = useLoadCasinoFeed({ feedNames });
  const categories = useCategories(feedNames);

  const { id: categoryId } =
    categories.find(({ id }) => id === feedCategory) || (categories[0] ?? {});

  const games = useGetFeedCategoryGames(categoryId, feedNames);

  return { games, isLoaded };
};

const usePromotionGamesPlex = ({
  feedNames,
  limit: initialLimit,
  feedCategory
}) => {
  const gamesData = useGamesData();

  const [isLoading, setIsLoading] = useState(false);
  const [promotionGamesIds, setPromotionsGamesIds] = useState([]);
  const loadPromotionCategory = useGetLoadFeedCategoryCallback();

  // (need this during transition period, when not all promotions/tournaments feeds have certain category set).
  // don't load the whole feed in case when feedCategory is passed
  const { isLoaded: isFeedLoaded } = useLoadCasinoFeed({
    feedNames: feedCategory ? undefined : feedNames,
    limit: initialLimit
  });

  const { isLoaded: isCategoryLoaded } = useLoadCategory({
    feedNames,
    categoryId: feedCategory,
    limit: initialLimit,
    isSetSingleFeedCategory: true
  });

  const actualIsLoaded = feedCategory ? isCategoryLoaded : isFeedLoaded;

  const promotionFeedCategories = useCategories(feedNames);

  const { games, id, total } = feedCategory
    ? (promotionFeedCategories.find((item) => item.id === feedCategory) ?? {})
    : // temporary fallback if "category" is not set for promotion/tournament in CMS
      (promotionFeedCategories[0] ?? {});

  useEffect(() => {
    if (actualIsLoaded) {
      setPromotionsGamesIds(games);
    }
  }, [actualIsLoaded]);

  const promotionGames = useMemo(() => {
    if (isEmpty(promotionGamesIds)) {
      return [];
    }

    return getGames(promotionGamesIds, gamesData);
  }, [promotionGamesIds, gamesData]);

  const gamesToLoadAfterInitial =
    total && total > initialLimit ? total - initialLimit : 0;

  const paginationLimit = Math.min(gamesToLoadAfterInitial, PLEX_LIMIT);

  const onShowAllGames = async () => {
    if (paginationLimit && actualIsLoaded && !isLoading) {
      setIsLoading(true);

      const newGamesIds = await loadPromotionCategory({
        feedName: feedNames,
        filter: {
          offset: promotionGamesIds.length,
          limit: paginationLimit,
          id
        },
        id
      });

      setPromotionsGamesIds((prev) => [...prev, ...newGamesIds]);
      setIsLoading(false);
    }
  };

  return {
    onShowAllGames,
    isLoaded: actualIsLoaded,
    games: promotionGames,
    isLoading
  };
};

export const usePromotionGames = IS_USE_PLEX_API
  ? usePromotionGamesPlex
  : usePromotionGamesNonPlex;
